import * as React from 'react';
import { Button, CheckboxWithHint, Modal } from '@ixicare/ui';
import { t } from '~i18n';
import { Spacer } from '~components/Utils/Spacer';

interface Props {
  /** Device types the push covers, for the line naming what is about to happen. */
  deviceTypeLabels: string[];
  /**
   * Whether to offer clearing per-device settings. Mirrors `can_edit_all_devices`, which the
   * backend requires on top of the site permission before it will accept the flag — offering it
   * without the permission would only produce a 403.
   */
  canClearDeviceSettings: boolean;
  onClose: () => void;
  /** Resolves when the configuration service has accepted the job; rejects on failure. */
  onConfirm: (clearDeviceSettings: boolean) => Promise<void>;
}

/**
 * Confirms the push, and carries the destructive option rather than putting it in the action bar
 * as a second button: the two differ by one word and one of them cannot be undone, so they must
 * not be adjacent targets.
 */
const SendConfigToDevicesModal: React.FC<Props> = ({
  deviceTypeLabels,
  canClearDeviceSettings,
  onClose,
  onConfirm,
}) => {
  const [clearDeviceSettings, setClearDeviceSettings] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const confirm = () => {
    setSending(true);
    onConfirm(clearDeviceSettings)
      .then(onClose)
      // The container reports the failure; the dialog stays open so the choice is not lost.
      .catch(() => setSending(false));
  };

  return (
    <Modal onClose={sending ? undefined : onClose} center>
      <h3 className="text-center mb-30">{t('site-device-config.send.title')}</h3>
      <p>
        {t('site-device-config.send.body')}{' '}
        <span style={{ color: 'var(--cloudy-blue)' }}>
          {t('site-device-config.send.scope', { types: deviceTypeLabels.join(', ') })}
        </span>
      </p>
      <Spacer size={32} />
      {canClearDeviceSettings && (
        <>
          <CheckboxWithHint
            id="clear-device-settings"
            tone="warning"
            label={t('site-device-config.send.clearLabel')}
            hint={t('site-device-config.send.clearHint')}
            checked={clearDeviceSettings}
            onCheckedChange={setClearDeviceSettings}
            disabled={sending}
          />
          <Spacer size={32} />
        </>
      )}
      <Button
        className="mb-15"
        isBlock
        type="button"
        buttonType={clearDeviceSettings ? 'error' : 'primary'}
        onClick={confirm}
        loading={sending}
        disabled={sending}
      >
        {t(clearDeviceSettings ? 'site-device-config.send.confirmClear' : 'site-device-config.send.confirm')}
      </Button>
      <Button onClick={onClose} disabled={sending} isBlock buttonType="link" type="button">
        {t('device-config.actions.cancel')}
      </Button>
    </Modal>
  );
};

export default SendConfigToDevicesModal;
